// @ts-nocheck
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import Remove from '@mui/icons-material/Remove';
import FormatIndentIncrease from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecrease from '@mui/icons-material/FormatIndentDecrease';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatClear from '@mui/icons-material/FormatClear';
import Save from '@mui/icons-material/Save';

const ICON_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  cursor: 'pointer',
  border: '1px solid #ddd',
  borderRadius: 4,
  backgroundColor: '#fff',
  color: '#555',
  padding: 0,
};

const ICON_SX = { fontSize: 18 };

const MultiLineEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value || '');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.selectionStart = len;
      textareaRef.current.selectionEnd = len;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    isCancelAfterEnd: () => false,
    isPopup: () => true,
  }));

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      // If text is selected, use Tab/Shift+Tab for indent/outdent
      const ta = textareaRef.current;
      if (ta && ta.selectionStart !== ta.selectionEnd) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          removeIndent();
        } else {
          addIndent();
        }
        return;
      }
      // No selection: Tab = save and close editor (without navigating to next cell)
      e.preventDefault();
      e.stopPropagation();
      props.api?.stopEditing(false);
    }
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
    if (e.key === 'b' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      toggleBold();
    }
  };

  const update = (newVal) => {
    setValue(newVal);
    if (props.onValueChange) props.onValueChange(newVal);
  };

  // Get the current line range in the textarea
  const getCurrentLines = () => {
    const ta = textareaRef.current;
    if (!ta) return { before: '', lines: [''], after: '', lineStart: 0, lineEnd: 0 };
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const lineEndIdx = value.indexOf('\n', end);
    const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx;
    const text = value.substring(lineStart, lineEnd);
    return {
      before: value.substring(0, lineStart),
      lines: text.split('\n'),
      after: value.substring(lineEnd),
      lineStart,
      lineEnd,
    };
  };

  const applyPrefix = (prefixFn) => {
    const { before, lines, after, lineStart } = getCurrentLines();
    const newLines = lines.map((line, i) => {
      // Strip any existing prefix (bullet, dash, number)
      const stripped = line.replace(/^(\s*)([\u2022\u25E6\u25AA]\s*|- \s*|\d+\.\s*|\d+\)\s*)/, '$1');
      return prefixFn(i) + stripped;
    });
    const joined = newLines.join('\n');
    update(before + joined + after);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current.selectionStart = lineStart;
      textareaRef.current.selectionEnd = lineStart + joined.length;
    }, 0);
  };

  const addBullets = () => applyPrefix(() => '\u2022 ');
  const addNumbers = () => applyPrefix((i) => `${i + 1}. `);
  const addDashes = () => applyPrefix(() => '- ');
  const clearFormat = () => applyPrefix(() => '');

  const addIndent = () => {
    const { before, lines, after, lineStart } = getCurrentLines();
    const newLines = lines.map(line => '    ' + line);
    const joined = newLines.join('\n');
    update(before + joined + after);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current.selectionStart = lineStart;
      textareaRef.current.selectionEnd = lineStart + joined.length;
    }, 0);
  };

  const removeIndent = () => {
    const { before, lines, after, lineStart } = getCurrentLines();
    const newLines = lines.map(line => line.replace(/^ {1,4}/, ''));
    const joined = newLines.join('\n');
    update(before + joined + after);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current.selectionStart = lineStart;
      textareaRef.current.selectionEnd = lineStart + joined.length;
    }, 0);
  };

  const toggleBold = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start === end) return; // No selection, do nothing

    const selected = value.substring(start, end);
    const before = value.substring(0, start);
    const after = value.substring(end);

    // Check if already bold-wrapped
    if (selected.startsWith('**') && selected.endsWith('**') && selected.length > 4) {
      // Unwrap: remove ** from both ends
      const unwrapped = selected.slice(2, -2);
      update(before + unwrapped + after);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = start;
        ta.selectionEnd = start + unwrapped.length;
      }, 0);
    } else if (before.endsWith('**') && after.startsWith('**')) {
      // Selection is inside ** markers — unwrap by removing surrounding **
      const newBefore = before.slice(0, -2);
      const newAfter = after.slice(2);
      update(newBefore + selected + newAfter);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = newBefore.length;
        ta.selectionEnd = newBefore.length + selected.length;
      }, 0);
    } else {
      // Wrap with **
      update(before + '**' + selected + '**' + after);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = start;
        ta.selectionEnd = end + 4;
      }, 0);
    }
  };


  return (
    <div
      style={{
        width: '460px',
        backgroundColor: '#fff',
        border: '2px solid #1565c0',
        borderRadius: '6px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}
      onMouseDown={(e) => {
        // Prevent AG-Grid from closing editor when clicking toolbar
        if (e.target.tagName === 'BUTTON') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '5px 8px',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #e0e0e0',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {props.data?.date_display && (
            <>
              <span style={{
                fontWeight: 600,
                fontSize: 13,
                color: '#1565c0',
                whiteSpace: 'nowrap',
                padding: '0 4px',
              }}>
                {props.data.date_display}
              </span>
              <span style={{ color: '#ddd', margin: '0 2px', fontSize: 14 }}>|</span>
            </>
          )}
          <button style={ICON_BTN} onClick={addBullets} title="Bullet list">
            <FormatListBulleted sx={ICON_SX} />
          </button>
          <button style={ICON_BTN} onClick={addNumbers} title="Numbered list">
            <FormatListNumbered sx={ICON_SX} />
          </button>
          <button style={ICON_BTN} onClick={addDashes} title="Dash list">
            <Remove sx={ICON_SX} />
          </button>
          <span style={{ color: '#ddd', margin: '0 2px', fontSize: 14 }}>|</span>
          <button style={ICON_BTN} onClick={addIndent} title="Indent (Tab)">
            <FormatIndentIncrease sx={ICON_SX} />
          </button>
          <button style={ICON_BTN} onClick={removeIndent} title="Outdent (Shift+Tab)">
            <FormatIndentDecrease sx={ICON_SX} />
          </button>
          <button style={ICON_BTN} onClick={toggleBold} title="Bold (Ctrl+B)">
            <FormatBold sx={ICON_SX} />
          </button>
          <span style={{ color: '#ddd', margin: '0 2px', fontSize: 14 }}>|</span>
          <button style={{ ...ICON_BTN, color: '#999' }} onClick={clearFormat} title="Clear formatting">
            <FormatClear sx={ICON_SX} />
          </button>
        </div>
        <button style={ICON_BTN} onClick={() => props.api?.stopEditing(false)} title="Save (Tab)">
          <Save sx={ICON_SX} />
        </button>
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => update(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          height: '180px',
          padding: '10px 12px',
          fontSize: '13px',
          fontFamily: 'inherit',
          border: 'none',
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
          lineHeight: '1.6',
          backgroundColor: '#fff',
        }}
        placeholder="Type your tasks here...&#10;&#10;Use toolbar above for bullet points or numbered lists.&#10;Press Enter for new line, Tab to save."
      />

      {/* Footer */}
      <div style={{
        padding: '4px 8px',
        backgroundColor: '#f5f5f5',
        borderTop: '1px solid #e0e0e0',
        fontSize: '11px',
        color: '#888',
        display: 'flex',
        justifyContent: 'space-between',
      }}>
        <span>Enter = new line | Ctrl+B = bold | Tab = save | Esc = cancel</span>
      </div>
    </div>
  );
});

export default MultiLineEditor;
